import React, { Fragment, useState, CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Clock, User, Target, MoveRight, MoveLeft, MessageSquare, Mic, Lock, RotateCcw, CircleAlert } from "lucide-react";
import { DynamicIcon, IconName } from 'lucide-react/dynamic';

import { useGetPersonaDetails, useGetScenarioWithPersonas, useStartConversation } from '../../../hooks/mbts-ux';

import { getScenarioColor } from '../../../helpers/misc.helper';
import { EConversationMode } from '../../../types/entities.enum';

import { ConversationModePicker } from '../../sub/mbts/ConversationModePicker';

export const MBTSPersonaDetails = () => {
    const { scenarioId, personaId } = useParams();

    const [isProcessing, setIsProcessing] = useState(false);
    const [isModePickerVisible, setIsModePickerVisible] = useState(false);
    const [isRestart, setIsRestart] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    const { data: details, isLoading, error: personaError } = useGetPersonaDetails(scenarioId ?? null, personaId ?? null);
    const { data: scenarioDetails, error: scenarioError } = useGetScenarioWithPersonas(scenarioId ?? null);
    const { mutate: startConversation } = useStartConversation();

    const oError = personaError || scenarioError;
    const error = (oError as any)?.response?.data;
    const entitlementError =
        error?.statusCode === 403
            ? error.message || 'You are not entitled to access this persona.'
            : null;

    const ctaLabel = !details
        ? null
        : details.status === 'not-started'
            ? 'Start Conversation'
            : details.status === 'in-progress'
                ? 'Continue Conversation'
                : 'Retry Conversation';

    const onCreateConversation = (mode: EConversationMode = EConversationMode.CHAT) => {
        if (isProcessing || !scenarioId || !personaId) return;

        startConversation({
            payload: {
                persona_id: personaId,
                mode,
                restart: isRestart,
            },
            scenarioId,
            setIsProcessing,
            setApiError,
        });
    };

    const openModePicker = (restart: boolean) => {
        if (isProcessing || !scenarioId || !personaId || !details) return;

        if (
            !restart
            && details.status !== 'completed'
            && (details.conversation?.mode === EConversationMode.CHAT || details.conversation?.mode === EConversationMode.AUDIO)
        ) {
            onCreateConversation(details.conversation.mode);
            return;
        };

        setIsRestart(restart);
        setIsModePickerVisible(true);
        setApiError(null);
    };

    const onSelectMode = (mode: EConversationMode) => {
        onCreateConversation(mode);
    };

    return (
        <div
            className='mbts_persona_details__page'
            style={{
                '--scenario-theme-color-main': getScenarioColor(scenarioDetails ?? null).main,
                '--scenario-theme-color-bg': getScenarioColor(scenarioDetails ?? null).bg
            } as CSSProperties}
        >
            {isLoading && !details && !entitlementError && !error && (
                <div className='d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <div className='list-loader' />
                    <p className='text-medium color--txt-secondary'>Loading persona details</p>
                </div>
            )}

            {!isLoading && !!entitlementError && (
                <div className='mbts_persona_details__top_sec mbts_persona_details__top_sec--restricted'>
                    <div className='mbts_persona_details__top_sec__icon mbts_persona_details__top_sec__icon--locked'>
                        <Lock />
                    </div>
                    <div>
                        <p className='text-3xl text-bold mb-xs text-center'>Access Restricted</p>
                        <p className='color--txt-tertiary text-center'>{entitlementError}</p>
                    </div>
                </div>
            )}
            {!isLoading && !entitlementError && !!error && (
                <div className='mt-2xl d-flex flex-column align-items-center justify-content-center gap-sm'>
                    <CircleAlert className='color--error w-3xl h-3xl' />
                    <p className='text-2xl text-semibold text-center'>Error occurred</p>
                    <p className='color--txt-tertiary text-center'>{error?.message || 'An error occurred while loading the personas.'}</p>
                </div>
            )}

            {!isLoading && !details && !entitlementError && !error && (
                <p className='text-lg text-semibold color--txt-tertiary text-center'>No persona details found!</p>
            )}
            {!isLoading && !!details && !entitlementError && !error && (
                <Fragment>
                    <div className='mbts_persona_details__top_sec'>
                        <div className='mbts_persona_details__top_sec__icon'>
                            <DynamicIcon name={details.icon as IconName} />
                        </div>
                        <div>
                            <p className='text-3xl text-bold mb-xs'>{details.person}</p>
                            <p className='color--txt-muted'>{details.title}</p>
                        </div>
                    </div>

                    <div className='card card--md card--radius-md'>
                        <p className='text-sm text-semibold color--txt-muted mb-lg tracking-wider'>CONVERSATION CONTEXT</p>
                        <div className='mbts_persona_details__convo_context'>
                            <div className='d-flex flex-column align-items-center gap-sm'>
                                <User />
                                <p className='text-sm text-semibold text-center'>Personality</p>
                                <p className='text-sm color--txt-disabled text-center'>{details.personality}</p>
                            </div>
                            <div className='d-flex flex-column align-items-center gap-sm'>
                                <Target />
                                <p className='text-sm text-semibold text-center'>Pushback Style</p>
                                <p className='text-sm color--txt-disabled text-center'>{details.pushback_style}</p>
                            </div>
                            <div className='d-flex flex-column align-items-center gap-sm'>
                                <Clock />
                                <p className='text-sm text-semibold text-center'>Duration</p>
                                <p className='text-sm color--txt-disabled text-center'>{details.duration}</p>
                            </div>
                        </div>
                        <div className='mbts_persona_details__opening'>
                            <p className='text-sm text-semibold color--txt-muted mb-lg tracking-wider'>THEIR OPENING STATEMENT</p>
                            <p className='text-sm text-italic color--txt-secondary'>"{details.opening}"</p>
                        </div>
                        <div className='mbts_persona_details__objectives'>
                            <p className='text-sm text-semibold color--txt-muted mb-lg tracking-wider'>YOUR OBJECTIVES:</p>
                            <ul>
                                {details.objectives.map((objective) => (
                                    <li key={objective.id} className='text-sm color--txt-tertiary'>{objective.title}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Fragment>
            )}

            <div className='mbts_persona_details__actions'>
                <Link to={`/mbts/${scenarioId}/personas`} className='link link--primary link--flex'>
                    <MoveLeft />
                    <span>Back to Overview</span>
                </Link>
                {!isLoading && !!details && !!ctaLabel && !entitlementError && !error && (
                    <div className='d-flex flex-column align-items-end gap-xs'>
                        <div className='d-flex align-items-center gap-sm'>
                            {details.status === 'in-progress' && (
                                <button
                                    className='btn btn--md btn--radius-md btn--secondary'
                                    onClick={() => openModePicker(true)}
                                    disabled={isProcessing}
                                >
                                    <RotateCcw />
                                    <span>Restart</span>
                                </button>
                            )}
                            <button
                                className='btn btn--md btn--radius-md btn--primary'
                                onClick={() => openModePicker(false)}
                                disabled={isProcessing}
                            >
                                {
                                    isProcessing
                                        ? <span>Starting...</span>
                                        : <Fragment>
                                            {details.status === 'in-progress' && details.conversation?.mode === EConversationMode.CHAT && (
                                                <MessageSquare aria-hidden='true' />
                                            )}
                                            {details.status === 'in-progress' && details.conversation?.mode === EConversationMode.AUDIO && (
                                                <Mic aria-hidden='true' />
                                            )}
                                            <span>{ctaLabel}</span>
                                        </Fragment>
                                }
                                {
                                    isProcessing
                                        ? <span className='btn-loader' />
                                        : <MoveRight />
                                }
                            </button>
                        </div>
                        {!!apiError && (
                            <p className='text-sm color--error'>{apiError}</p>
                        )}
                    </div>
                )}
            </div>

            <ConversationModePicker
                visible={isModePickerVisible}
                setVisible={setIsModePickerVisible}
                isProcessing={isProcessing}
                status={details?.status ?? null}
                restart={isRestart}
                onSelectMode={onSelectMode}
                apiError={apiError}
                setApiError={setApiError}
            />
        </div>
    );
};
